// src/api/AnalyticsApi.js

import axios from 'axios';
import { handleApiError } from './handleApiError.js'; // Use the centralized error handler

// Retrieve the base URL from environment variables, with a fallback for local development
const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

/**
 * Fetches data for the smart dashboard by sending a configuration object.
 *
 * This function communicates with the backend's single, powerful visualization endpoint.
 * It sends a detailed payload specifying the desired analysis and receives a
 * complete, chart-ready data structure in response.
 *
 * @param {string} token - The user's Firebase auth token for authentication.
 * @param {object} visualizationPayload - The configuration object for the desired chart.
 *   @property {object} time_range - The start and end dates.
 *     @property {string} start_date - e.g., "2025-10-22"
 *     @property {string} end_date - e.g., "2025-10-24"
 *   @property {string} metric - e.g., "total_cost", "api_calls"
 *   @property {string|null} time_grouping - e.g., "day", "week", "month"
 *   @property {string|null} primary_dimension - e.g., "analysis_type", "model_name"
 *   @property {string|null} secondary_dimension - e.g., "call_outcome"
 *   @property {string|null} userId - Optional. For Admins to view a specific user's data.
 * @returns {Promise<object>} A promise that resolves to either:
 *   - { status: true, data: { ... } } on success, where `data` is the full response object
 *     from the backend (containing chart recommendations, title, and data).
 *   - { status: false, message: string, error: string } on failure.
 */
export const fetchVisualizationData = async (token, visualizationPayload) => {
  const url = `${baseUrl}/api/analytics/visualization`;
  console.debug(
    '[fetchVisualizationData] Attempting to fetch analytics data with payload:',
    visualizationPayload,
  );

  try {
    const response = await axios.post(url, visualizationPayload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    console.debug(
      '[fetchVisualizationData] Successfully fetched and processed analytics data.',
    );
    // The backend's response is already well-structured, so we pass it directly.
    return { status: true, data: response.data };
  } catch (error) {
    // Delegate all error handling to the centralized, consistent handler.
    return handleApiError(error, 'fetchVisualizationData');
  }
};

/**
 * Fetches the invoice/billing history for the authenticated user.
 *
 * @param {string} token - The user's Firebase auth token.
 * @returns {Promise<object>} { status: true, data: [...] } or error object.
 */
export const fetchUserInvoices = async (token) => {
  const url = `${baseUrl}/api/analytics/invoices`;
  console.debug('[fetchUserInvoices] Fetching billing history...');

  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    console.debug('[fetchUserInvoices] Successfully fetched invoice data.');
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'fetchUserInvoices');
  }
};

export const fetchCompanyWalletTransactions = async (token) => {
  const url = `${baseUrl}/api/analytics/wallet-transactions`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'fetchCompanyWalletTransactions');
  }
};

// src/api/AnalyticsApi.js
// ... [Keep existing code] ...

export const createStripeCheckoutSession = async (token, amount_usd) => {
  const url = `${baseUrl}/api/billing/create-checkout-session`;
  try {
    const response = await axios.post(
      url,
      { amount_usd },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'createStripeCheckoutSession');
  }
};

export const createStripeSetupSession = async (token) => {
  const url = `${baseUrl}/api/billing/create-setup-session`;
  try {
    const response = await axios.post(
      url,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'createStripeSetupSession');
  }
};

export const fetchAutoTopupConfig = async (token) => {
  const url = `${baseUrl}/api/billing/auto-topup-config`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'fetchAutoTopupConfig');
  }
};

export const updateAutoTopupConfig = async (token, configData) => {
  const url = `${baseUrl}/api/billing/auto-topup-config`;
  try {
    const response = await axios.put(url, configData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'updateAutoTopupConfig');
  }
};
